// Motor e vocabulário das mensagens personalizadas (robô por palavra-chave +
// notificações de status + resumo do pedido). Tudo aqui é lógica pura, sem
// JSX — a tela consome estas funções/constantes. O contrato do resumo
// ({{#if cond}}, variações, seções) é o mesmo que o backend/UniService renderiza
// no envio real, então precisa se manter idêntico em comportamento.

// ─── Tipos de resposta ──────────────────────────────────────────────────────
// Cada answer do chatbot cai em exatamente um tipo, identificado pelas flags
// booleanas (firstContact, isPending, ...). "keyword" é o tipo padrão: nenhuma
// flag de status ligada.
export const RESPONSE_TYPES = [
  {
    key: 'initial',
    label: 'Inicial',
    hex: '#a25dd1',
    description: 'Enviada automaticamente no primeiro contato do cliente.',
    match: (r) => !!r.firstContact,
  },
  {
    key: 'pending',
    label: 'Pedido aceito',
    hex: '#06b1d9',
    description: 'Enviada automaticamente quando o pedido é recebido.',
    match: (r) => !!r.isPending,
  },
  {
    key: 'progress',
    label: 'Em preparação',
    hex: '#f59e0b',
    description: 'Enviada automaticamente quando o pedido entra em preparação.',
    match: (r) => !!r.isProgress,
  },
  {
    key: 'delivered',
    label: 'Saiu para entrega',
    hex: '#16a34a',
    description: 'Enviada automaticamente quando o pedido sai para entrega.',
    match: (r) => !!r.isDelivered,
  },
  {
    key: 'pickup',
    label: 'Pronto para retirada',
    hex: '#eab308',
    description: 'Enviada automaticamente quando o pedido está pronto para retirada.',
    match: (r) => !!r.isPickUp,
  },
  {
    key: 'finished',
    label: 'Finalizado',
    hex: '#16a34a',
    description: 'Enviada automaticamente quando o pedido é finalizado.',
    match: (r) => !!r.isFinished,
  },
  {
    key: 'resume',
    label: 'Resumo do pedido',
    hex: '#dc2626',
    description: 'Enviada automaticamente como resumo do pedido recebido.',
    match: (r) => !!r.isResume,
  },
  {
    key: 'keyword',
    label: 'Palavras-chave',
    hex: '#3b82f6',
    description: 'Enviada quando o cliente digita uma das palavras-chave configuradas.',
    match: (r) =>
      !r.firstContact &&
      !r.isPending &&
      !r.isProgress &&
      !r.isDelivered &&
      !r.isFinished &&
      !r.isPickUp &&
      !r.isResume,
  },
];

export const getResponseType = (response) =>
  RESPONSE_TYPES.find((t) => t.match(response)) ?? null;

// Um status é "de pedido" (automático): não usa palavra-chave nem é o resumo.
export const isStatusMessage = (r) =>
  !!(r.isPending || r.isProgress || r.isDelivered || r.isFinished || r.isPickUp);

export const isKeywordMessage = (r) =>
  getResponseType(r)?.key === 'keyword';

// ─── Placeholders (variáveis) ───────────────────────────────────────────────
// Disponíveis em qualquer mensagem.
export const GENERAL_PLACEHOLDERS = [
  { value: '{{nome_cliente}}', label: 'Nome do cliente', tooltip: 'Nome do cliente que está conversando' },
  { value: '{{saudacao}}', label: 'Saudação', tooltip: 'Saudação automática conforme o horário: Bom dia / Boa tarde / Boa noite' },
  { value: '{{dividir_texto}}', label: 'Separar mensagens', tooltip: 'Quebra o texto em várias mensagens enviadas em sequência' },
  { value: '{{cardapio}}', label: 'Cardápio', tooltip: 'Envia o cardápio completo no chat' },
  { value: '{{link_cardapio}}', label: 'Link do cardápio', tooltip: 'Link público do cardápio digital' },
  { value: '{{endereco_loja}}', label: 'Endereço da loja', tooltip: 'Endereço do estabelecimento' },
  { value: '{{numero_loja}}', label: 'Telefone da loja', tooltip: 'Telefone do estabelecimento' },
  { value: '{{formas_pagamento}}', label: 'Formas de pagamento', tooltip: 'Lista das formas de pagamento aceitas' },
  { value: '{{horario_funcionamento}}', label: 'Horários', tooltip: 'Horários de funcionamento do dia' },
];

// Exclusivas do resumo do pedido (isResume).
export const RESUME_PLACEHOLDERS = [
  { value: '{{numero_pedido}}', label: 'Nº do pedido', tooltip: 'Identificador do pedido' },
  { value: '{{data_pedido}}', label: 'Data do pedido', tooltip: 'Data em que o pedido foi feito' },
  { value: '{{hora_pedido}}', label: 'Hora do pedido', tooltip: 'Horário em que o pedido foi feito' },
  { value: '{{telefone_cliente}}', label: 'Telefone do cliente', tooltip: 'Telefone informado pelo cliente' },
  { value: '{{endereco_cliente}}', label: 'Endereço do cliente', tooltip: 'Endereço de entrega do cliente' },
  { value: '{{forma_pagamento_pedido}}', label: 'Pagamento escolhido', tooltip: 'Forma de pagamento selecionada para este pedido' },
  { value: '{{produtos_pedido}}', label: 'Itens do pedido', tooltip: 'Lista formatada de todos os itens do pedido' },
  { value: '{{subtotal_pedido}}', label: 'Subtotal', tooltip: 'Valor dos produtos sem taxas' },
  { value: '{{taxa_entrega}}', label: 'Taxa de entrega', tooltip: 'Valor cobrado pela entrega' },
  { value: '{{total_pedido}}', label: 'Total', tooltip: 'Valor total do pedido' },
  { value: '{{troco_para}}', label: 'Troco para', tooltip: 'Valor para o qual o cliente precisa de troco' },
  { value: '{{data_envio}}', label: 'Data de envio', tooltip: 'Data e hora do envio da mensagem ao cliente' },
];

export const PLACEHOLDER_BY_KEY = {};
[...GENERAL_PLACEHOLDERS, ...RESUME_PLACEHOLDERS].forEach((p) => {
  PLACEHOLDER_BY_KEY[p.value.replace(/[{}]/g, '')] = p;
});

export const PLACEHOLDER_LABELS = {
  quantidade: 'Quantidade',
  descricao: 'Descrição',
  tamanho: 'Tamanho',
  borda: 'Borda',
  precoUnitario: 'Preço unitário',
  subtotal: 'Subtotal',
  sabores: 'Sabores',
  adicionais: 'Adicionais',
  montagens: 'Montagens',
  observacao: 'Observação',
  rotulo: 'Rótulo',
  texto: 'Texto',
  logradouro: 'Logradouro',
  numero: 'Número',
  complemento: 'Complemento',
  bairro: 'Bairro',
  cidade: 'Cidade',
  uf: 'UF',
  valorTroco: 'Valor do troco',
};

// ─── Helpers de tempo (Drawer de configurações) ─────────────────────────────
export const milissegundosParaFormatoHora = (ms) => {
  const segundos = Math.floor(ms / 1000);
  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  const segundosRestantes = segundos % 60;
  let out = '';
  if (horas > 0) out += `${horas}h `;
  if (minutos > 0 || horas > 0) out += `${minutos}min `;
  out += `${segundosRestantes}s`;
  return out;
};

export const milissegundosParaMinutos = (ms) => ms / (60 * 1000);
export const minutosParaMilissegundos = (min) => min * 60 * 1000;

export const filtrarNumerosDoTexto = (texto) => {
  const numeros = String(texto).match(/\d+/g);
  const numeroCombinado = numeros ? parseInt(numeros.join(''), 10) : null;
  return numeroCombinado > 1440 ? 1440 : numeroCombinado;
};

// ─── Engine de template do resumo ───────────────────────────────────────────
// Suporta {{var}} e {{#if cond}}...{{/if}}. Condições derivam do tipo de
// entrega + forma de pagamento do pedido.
const conditionsFromInfo = (info) => {
  const tipo = String(info.TipoEntrega || '').toUpperCase();
  const pag = String(info.FormaPagamento || '').toUpperCase();
  const taxa = Number(info.TaxaEntrega || 0);
  const troco = Number(info.Troco || 0);
  const total = Number(info.ValorTotal || 0) + taxa;
  return {
    entrega: ['EN', 'AG'].includes(tipo),
    retirada: ['RE', 'AGRE'].includes(tipo),
    consumo_local: tipo === 'LOCAL',
    agendado: ['AG', 'AGRE'].includes(tipo),
    dinheiro: pag === 'DINHEIRO',
    cartao: ['CARTAO', 'CREDITO', 'DEBITO'].includes(pag),
    pix: pag === 'PIX',
    vale: ['VR', 'VA'].includes(pag),
    troco: pag === 'DINHEIRO' && troco > total,
    taxa_entrega: taxa > 0,
    endereco: !!(info.Logradouro || info.EnderecoSemCadastro),
  };
};

const resolveBlocks = (text, conditions) => {
  if (!text) return '';
  return text.replace(
    /\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_, key, body) => (conditions[key] ? body : ''),
  );
};

const fillTokens = (text, vars) => {
  if (!text) return '';
  return String(text).replace(/\{\{(\w+)\}\}/g, (_, k) => {
    const v = vars[k];
    return v === undefined || v === null ? '' : String(v);
  });
};

const formatBRL = (n) => `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`;

export const safeParseVariations = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw) ?? {};
  } catch {
    return {};
  }
};

const buildItemPreview = (item, variations) => {
  const v = variations || {};
  const tamanho = item.Tamanho ? fillTokens(v.tamanho, { descricao: item.Tamanho }) : '';
  const borda = item.Borda?.Descricao ? fillTokens(v.borda, { descricao: item.Borda.Descricao }) : '';
  const sabores = (item.Sabores || []).map((s) => fillTokens(v.sabor, { rotulo: s.label })).join('');
  const adicionais = (item.Adicionais || [])
    .map((a) => fillTokens(v.adicional, { quantidade: a.amount ?? 1, rotulo: a.label }))
    .join('');
  const montagens = (item.Montagem || [])
    .map((m) => fillTokens(v.montagem, { quantidade: m.quantidade ?? 1, rotulo: m.label }))
    .join('');
  const observacao = item.Observacao ? fillTokens(v.observacao, { texto: item.Observacao }) : '';
  const valor = parseFloat(item.Valor || 0);
  const qtd = parseInt(item.Quantidade, 10) || 0;
  return fillTokens(v.item, {
    quantidade: qtd,
    descricao: item.Descricao,
    tamanho,
    borda,
    precoUnitario: formatBRL(valor),
    subtotal: formatBRL(valor * qtd),
    sabores,
    adicionais,
    montagens,
    observacao,
  });
};

const renderEnderecoPreview = (info, v) => {
  if (info.Logradouro) {
    return fillTokens(v.endereco, {
      logradouro: info.Logradouro || '',
      numero: info.Numero ? `Nº ${info.Numero}` : '',
      complemento: info.Complemento ? `, ${info.Complemento}` : '',
      bairro: info.Bairro ? `, ${info.Bairro}` : '',
      cidade: info.Cidade ? `, ${info.Cidade}` : '',
      uf: info.Uf ? `/${info.Uf}` : '',
    });
  }
  if (info.EnderecoSemCadastro) {
    return fillTokens(v.enderecoSemCadastro || '{{texto}}', { texto: info.EnderecoSemCadastro });
  }
  return v.enderecoSemDados || 'Não informado';
};

export const renderResumePreview = (text, variationsRaw, scenario) => {
  const variations = safeParseVariations(variationsRaw);
  const { info, pedido } = scenario;
  const conditions = conditionsFromInfo(info);

  const subtotal = parseFloat(info.ValorTotal || 0);
  const taxa = parseFloat(info.TaxaEntrega || 0);
  const total = subtotal + taxa;

  const itemSep = variations.separadorItens || '\n';
  const produtos = (pedido || []).map((it) => buildItemPreview(it, variations)).join(itemSep);

  const vars = {
    numero_pedido: info.Id || '',
    data_pedido: '06/05/2026',
    hora_pedido: '14:30',
    nome_cliente: info.Nome || 'Cliente',
    telefone_cliente: info.Telefone || '',
    endereco_cliente: renderEnderecoPreview(info, variations),
    forma_pagamento_pedido:
      variations.formasPagamento?.[String(info.FormaPagamento || '').toUpperCase()] ||
      info.FormaPagamento ||
      '',
    produtos_pedido: produtos,
    subtotal_pedido: formatBRL(subtotal),
    taxa_entrega: formatBRL(taxa),
    total_pedido: formatBRL(total),
    troco_para: conditions.troco ? formatBRL(info.Troco) : '',
    data_envio: '06/05/2026 14:30',
  };

  const step1 = resolveBlocks(text, conditions);
  const step2 = fillTokens(step1, vars);
  return step2.replace(/\n{3,}/g, '\n\n').trim();
};

// Cenários de pré-visualização do resumo.
export const RESUME_PREVIEW_SCENARIOS = [
  {
    key: 'entrega-dinheiro',
    label: '🛵 Entrega + Dinheiro c/ troco',
    info: {
      Id: 1234,
      TipoEntrega: 'EN',
      FormaPagamento: 'DINHEIRO',
      Troco: 100,
      ValorTotal: 75.5,
      TaxaEntrega: 5,
      Logradouro: 'Rua das Flores',
      Numero: '123',
      Complemento: 'Apto 4B',
      Bairro: 'Centro',
      Cidade: 'São Paulo',
      Uf: 'SP',
      Nome: 'Maria Silva',
      Telefone: '(11) 99999-9999',
    },
    pedido: [
      {
        Quantidade: '1',
        Descricao: 'Pizza Calabresa',
        Tamanho: 'Grande',
        Borda: { Descricao: 'Catupiry' },
        Valor: 70.5,
        Sabores: [{ label: 'Mussarela' }],
        Adicionais: [{ label: 'Bacon', amount: 1 }],
        Montagem: [],
        Observacao: 'sem cebola',
      },
      { Quantidade: '1', Descricao: 'Refrigerante 2L', Valor: 5, Sabores: [], Adicionais: [], Montagem: [] },
    ],
  },
  {
    key: 'retirada-cartao',
    label: '🏪 Retirada + Cartão',
    info: {
      Id: 1235,
      TipoEntrega: 'RE',
      FormaPagamento: 'CREDITO',
      ValorTotal: 60,
      TaxaEntrega: 0,
      Nome: 'João Souza',
      Telefone: '(11) 88888-8888',
    },
    pedido: [{ Quantidade: '2', Descricao: 'Hambúrguer Artesanal', Valor: 30, Sabores: [], Adicionais: [], Montagem: [] }],
  },
  {
    key: 'local-pix',
    label: '🍽 Consumo no local + PIX',
    info: {
      Id: 1236,
      TipoEntrega: 'LOCAL',
      FormaPagamento: 'PIX',
      ValorTotal: 45,
      TaxaEntrega: 0,
      Nome: 'Ana Costa',
      Telefone: '(11) 77777-7777',
    },
    pedido: [
      { Quantidade: '1', Descricao: 'Prato Executivo', Valor: 45, Sabores: [], Adicionais: [], Montagem: [], Observacao: 'sem alface' },
    ],
  },
];

// ─── Seções do resumo ───────────────────────────────────────────────────────
// O usuário edita blocos nomeados; o sistema decide quando mostrar (condition).
// group controla o espaçamento: dentro do grupo \n, entre grupos \n\n.
export const RESUME_SECTIONS = [
  { key: 'topo', group: 'topo', title: 'Topo da mensagem', description: 'Aparece sempre, no início.', icon: '🧾', condition: null, placeholders: ['numero_pedido', 'data_pedido', 'hora_pedido'], default: '*🧾 Pedido #{{numero_pedido}}*\n🗓 {{data_pedido}}  ⏰ {{hora_pedido}}' },
  { key: 'agendado', group: 'topo', title: 'Pedido agendado', description: 'Só aparece quando o pedido é agendado.', icon: '⏳', condition: 'agendado', placeholders: [], default: '⏳ *Pedido agendado*' },
  { key: 'cliente', group: 'cliente', title: 'Dados do cliente', description: 'Aparece sempre. Mostra o nome e telefone.', icon: '👤', condition: null, placeholders: ['nome_cliente', 'telefone_cliente'], default: '*👤 Cliente*\n{{nome_cliente}}\n📞 {{telefone_cliente}}' },
  { key: 'entrega', group: 'cliente', title: 'Endereço de entrega', description: 'Só aparece em pedidos para entrega.', icon: '📍', condition: 'entrega', placeholders: ['endereco_cliente'], default: '📍 {{endereco_cliente}}' },
  { key: 'retirada', group: 'cliente', title: 'Retirada no estabelecimento', description: 'Só aparece quando o cliente vai retirar no local.', icon: '🏪', condition: 'retirada', placeholders: [], default: '🏪 Retirada no estabelecimento' },
  { key: 'consumo_local', group: 'cliente', title: 'Consumo no local', description: 'Só aparece quando o cliente vai comer no estabelecimento.', icon: '🍽', condition: 'consumo_local', placeholders: [], default: '🍽 Consumo no local' },
  { key: 'pagamento', group: 'pagamento', title: 'Forma de pagamento', description: 'Aparece sempre.', icon: '💰', condition: null, placeholders: ['forma_pagamento_pedido'], default: '*💰 Pagamento*\n{{forma_pagamento_pedido}}' },
  { key: 'troco', group: 'pagamento', title: 'Linha de troco', description: 'Só aparece em pagamento com dinheiro quando o cliente precisa de troco.', icon: '💵', condition: 'troco', placeholders: ['troco_para'], default: '💵 Troco para: {{troco_para}}' },
  { key: 'subtotal', group: 'valores', title: 'Subtotal', description: 'Aparece sempre. Mostra o valor dos produtos.', icon: '💲', condition: null, placeholders: ['subtotal_pedido'], default: '*💲 Valores*\nProdutos: {{subtotal_pedido}}' },
  { key: 'taxa_entrega', group: 'valores', title: 'Taxa de entrega', description: 'Só aparece quando o pedido tem taxa de entrega.', icon: '🚚', condition: 'taxa_entrega', placeholders: ['taxa_entrega'], default: 'Entrega: {{taxa_entrega}}' },
  { key: 'total', group: 'valores', title: 'Total', description: 'Aparece sempre.', icon: '💲', condition: null, placeholders: ['total_pedido'], default: '*Total: {{total_pedido}}*' },
  { key: 'itens', group: 'itens', title: 'Itens do pedido', description: 'Aparece sempre. Lista de produtos.', icon: '📝', condition: null, placeholders: ['produtos_pedido'], default: '*📝 Itens do pedido*\n-----------------------------------\n{{produtos_pedido}}\n-----------------------------------' },
  { key: 'rodape', group: 'rodape', title: 'Rodapé', description: 'Aparece sempre, no final.', icon: '🔚', condition: null, placeholders: ['data_envio'], default: '`Data envio: {{data_envio}}`' },
];

export const defaultResumeSections = () => {
  const out = {};
  RESUME_SECTIONS.forEach((s) => {
    out[s.key] = s.default;
  });
  return out;
};

// Recompõe o `text` final a partir do mapa de seções (condicionais wrapeadas em
// {{#if X}}...{{/if}}; \n dentro do grupo, \n\n entre grupos).
export const composeResumeText = (sections) => {
  const groups = [];
  let currentGroupName = null;
  RESUME_SECTIONS.forEach((s) => {
    if (s.group !== currentGroupName) {
      groups.push({ name: s.group, items: [] });
      currentGroupName = s.group;
    }
    groups[groups.length - 1].items.push(s);
  });

  const groupTexts = groups.map((g) => {
    const parts = g.items
      .map((s) => {
        const content = (sections?.[s.key] ?? '').trim();
        if (!content) return null;
        return s.condition ? `{{#if ${s.condition}}}${content}{{/if}}` : content;
      })
      .filter(Boolean);
    return parts.join('\n');
  });

  return groupTexts.filter((g) => g.length > 0).join('\n\n');
};

// Lê o mapa de seções a partir de variations.sections, completando com os
// defaults do schema (caso novas seções tenham sido adicionadas).
export const readResumeSections = (variationsRaw) => {
  const variations = safeParseVariations(variationsRaw);
  const merged = defaultResumeSections();
  if (variations.sections && typeof variations.sections === 'object') {
    Object.keys(variations.sections).forEach((k) => {
      if (k in merged) merged[k] = variations.sections[k];
    });
  }
  return merged;
};

// ─── Grupos de variações (editor avançado do resumo) ────────────────────────
export const VARIATION_GROUPS = [
  {
    key: 'rotulos',
    icon: '🏷',
    title: 'Rótulos exibidos no resumo',
    description: 'Como o nome de cada tipo de entrega e forma de pagamento aparece para o cliente.',
    defaultOpen: true,
    fields: [
      {
        key: 'tiposEntrega',
        label: 'Tipos de entrega',
        type: 'object',
        fields: [
          { key: 'EN', label: 'Entrega no endereço' },
          { key: 'RE', label: 'Retirada no estabelecimento' },
          { key: 'LOCAL', label: 'Consumo no local' },
          { key: 'AG', label: 'Agendado (entrega)' },
          { key: 'AGRE', label: 'Agendado (retirada)' },
        ],
      },
      {
        key: 'formasPagamento',
        label: 'Formas de pagamento',
        type: 'object',
        fields: [
          { key: 'DINHEIRO', label: 'Dinheiro' },
          { key: 'CARTAO', label: 'Cartão' },
          { key: 'CREDITO', label: 'Cartão de crédito' },
          { key: 'DEBITO', label: 'Cartão de débito' },
          { key: 'PIX', label: 'PIX' },
          { key: 'VR', label: 'Vale Refeição' },
          { key: 'VA', label: 'Vale Alimentação' },
        ],
      },
    ],
  },
  {
    key: 'item',
    icon: '📦',
    title: 'Como aparece cada item do pedido',
    description:
      'Define o formato de cada produto na lista do resumo. Os campos menores (tamanho, borda, sabor, adicional, montagem, observação) são encaixados automaticamente dentro do modelo principal quando o produto tiver esses dados.',
    defaultOpen: false,
    fields: [
      { key: 'item', label: 'Modelo de cada produto', description: 'Como cada produto da lista é montado. Os blocos extras só aparecem quando o produto realmente tiver tamanho, borda, sabores, etc.', example: '*1x Pizza Calabresa* Grande (`Borda Catupiry`)\nR$ 50,00 = R$ 50,00', type: 'textarea', rows: 3, chips: ['quantidade', 'descricao', 'tamanho', 'borda', 'precoUnitario', 'subtotal', 'sabores', 'adicionais', 'montagens', 'observacao'] },
      { key: 'tamanho', label: 'Tamanho do produto', description: 'Aparece logo depois do nome quando o produto tem tamanho selecionado.', example: ' Grande', type: 'text', chips: ['descricao'] },
      { key: 'borda', label: 'Borda da pizza', description: 'Mostrado entre parênteses quando a pizza tem borda recheada.', example: ' (`Borda Catupiry`)', type: 'text', chips: ['descricao'] },
      { key: 'sabor', label: 'Sabores extras', description: 'Uma linha para cada sabor extra escolhido em pizzas multissabor.', example: '\n`+Sabor: Mussarela`', type: 'text', chips: ['rotulo'] },
      { key: 'adicional', label: 'Adicionais (extras pagos)', description: 'Uma linha para cada adicional pago escolhido pelo cliente (ex.: bacon, queijo extra).', example: '\n`1x Extra Bacon`', type: 'text', chips: ['quantidade', 'rotulo'] },
      { key: 'montagem', label: 'Itens de montagem', description: 'Uma linha para cada item de montagem do produto (escolhas em combos personalizáveis).', example: '\n`2x Adicional Refrigerante`', type: 'text', chips: ['quantidade', 'rotulo'] },
      { key: 'observacao', label: 'Observação do cliente', description: 'Aparece quando o cliente escreve uma observação para aquele produto específico (ex.: "sem cebola").', example: '\n_sem cebola_', type: 'text', chips: ['texto'] },
    ],
  },
  {
    key: 'extra',
    icon: '📍',
    title: 'Endereço e troco',
    description: 'Como o endereço do cliente e a linha de troco aparecem no resumo. Cada item do endereço é usado conforme o cliente tenha cadastro completo, parcial ou nenhum.',
    defaultOpen: false,
    fields: [
      { key: 'endereco', label: 'Endereço completo (cliente cadastrado)', description: 'Formato usado quando o cliente já tem o endereço cadastrado no sistema com todos os campos.', example: 'Rua das Flores | Nº 123 | Comp. Apto 4B, Centro', type: 'textarea', rows: 2, chips: ['logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'] },
      { key: 'enderecoSemCadastro', label: 'Endereço enviado por texto', description: 'Usado quando o cliente envia o endereço como texto livre, sem cadastrar. Normalmente basta {{texto}} para repetir o que ele escreveu.', example: 'Rua das Flores, 123 - Centro', type: 'text', chips: ['texto'] },
      { key: 'enderecoSemDados', label: 'Quando não há endereço', description: 'Texto exibido quando o pedido não tem endereço algum (ex.: retirada no balcão sem dados de entrega).', example: 'Não informado', type: 'text' },
      { key: 'linhaTroco', label: 'Linha de troco', description: 'Aparece logo abaixo da forma de pagamento somente quando o cliente paga em dinheiro e pediu troco.', example: 'Troco para: R$ 100,00', type: 'text', chips: ['valorTroco'] },
    ],
  },
];
